import { HoverHelp } from '@/tableControls'
import type { ProposalResponse, TierBreakdown } from '@/types/waterfall'
import { MEMBERS } from '@/types/waterfall'
import {
  buildupDetailRowsForTier,
  buildupRemainingRowForTier,
  type BuildupRow,
  type BuildupRowKind,
} from '@/utils/waterfallBuildup'

const fmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
})

const ROW_STYLES: Record<BuildupRowKind, string> = {
  cash_in: 'text-gray-500 italic',
  true_up: 'text-gray-600',
  current_period: 'text-gray-600',
  cap_adjustment: 'text-amber-700',
  subtotal: 'font-medium text-gray-800',
  remaining: 'text-gray-500 italic',
}

const CAP_ADJUSTMENT_HELP =
  'When available cash in a tier is less than the sum of accrual corrections and current-period allocations, the floor-at-zero rule caps what can be paid. This row is the residual that reconciles those capped components to the tier subtotal (often from rounding on plug cells).'

interface TierBuildupRowsProps {
  tierKey: keyof TierBreakdown
  result: ProposalResponse
  proposalGrid: TierBreakdown
  labelCellClass: string
  numCellClass: string
  totalCellClass: string
}

function formatRowValue(row: BuildupRow, member: (typeof MEMBERS)[number]): string {
  if (row.kind === 'cash_in' || row.kind === 'remaining') {
    if (member !== 'JR') return ''
    return fmt.format(row.total)
  }
  const value = row.amounts[member]
  if (Math.abs(value) <= 0.005) return '—'
  return fmt.format(value)
}

function formatRowTotal(row: BuildupRow): string {
  return fmt.format(row.total)
}

function BuildupRowCells({
  row,
  labelCellClass,
  numCellClass,
  totalCellClass,
}: {
  row: BuildupRow
  labelCellClass: string
  numCellClass: string
  totalCellClass: string
}) {
  const rowBg = row.kind === 'remaining' ? 'bg-gray-50' : ''
  const stickyLabelClass = rowBg
    ? `${labelCellClass.replace(' bg-white', '').replace(' group-hover:bg-gray-50', '')} ${rowBg}`
    : labelCellClass

  return (
    <tr className={`text-xs border-t border-gray-100 ${rowBg}`}>
      <td
        className={`${stickyLabelClass} ${ROW_STYLES[row.kind]}`}
        style={{ paddingLeft: `${12 + row.indent * 16}px` }}
      >
        <span className="inline-flex items-center">
          {row.kind === 'cap_adjustment' ? (
            <HoverHelp
              label={row.label}
              aria-label="Cash cap adjustment explanation"
              className="not-italic"
            >
              <p>{CAP_ADJUSTMENT_HELP}</p>
            </HoverHelp>
          ) : (
            row.label
          )}
        </span>
      </td>
      {MEMBERS.map((member) => (
        <td key={member} className={`${numCellClass} ${rowBg} ${ROW_STYLES[row.kind]}`}>
          {formatRowValue(row, member) || '—'}
        </td>
      ))}
      <td className={`${totalCellClass} ${rowBg} ${ROW_STYLES[row.kind]}`}>
        {formatRowTotal(row)}
      </td>
    </tr>
  )
}

export function TierBuildupDetailRows({
  tierKey,
  result,
  proposalGrid,
  labelCellClass,
  numCellClass,
  totalCellClass,
}: TierBuildupRowsProps) {
  const rows = buildupDetailRowsForTier(tierKey, result, proposalGrid)

  return (
    <>
      {rows.map((row, index) => (
        <BuildupRowCells
          key={`${tierKey}-detail-${index}`}
          row={row}
          labelCellClass={labelCellClass}
          numCellClass={numCellClass}
          totalCellClass={totalCellClass}
        />
      ))}
    </>
  )
}

export function TierBuildupRemainingRow({
  tierKey,
  result,
  proposalGrid,
  labelCellClass,
  numCellClass,
  totalCellClass,
}: TierBuildupRowsProps) {
  const row = buildupRemainingRowForTier(tierKey, result, proposalGrid)
  if (!row) return null

  return (
    <BuildupRowCells
      row={row}
      labelCellClass={labelCellClass}
      numCellClass={numCellClass}
      totalCellClass={totalCellClass}
    />
  )
}

/** @deprecated Use TierBuildupDetailRows and TierBuildupRemainingRow instead. */
export default function TierBuildupRows(props: TierBuildupRowsProps) {
  return (
    <>
      <TierBuildupDetailRows {...props} />
      <TierBuildupRemainingRow {...props} />
    </>
  )
}